<template>
  <div
    id="_konva"
    tabindex="-1"
    data-workbench="true"
    data-shape-keyboard="true"
    :style="{
      width: width + 'px',
      height: height + 'px'
    }"
  />
</template>

<script lang="ts" setup>
import { computed, onDeactivated, onMounted, watch } from 'vue';
import KonvaService from '@/services/konva';
import { EventType } from '@/utils/emitter.utils';
import { debounce } from 'lodash-es';
import { useEditorStore } from '@/store/use-editor-store';
import { Shape, ShapeType } from '@/services/shape';
import {
  lockedIdsInCurShapeTree,
  useEmitterListener,
  useGlobalShortcuts,
  watchfaceExporting,
  watchfaceImageLoading,
  watchfaceOpening
} from '@/hooks';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { useRoute } from 'vue-router';
const props = defineProps({
  width: {
    type: Number,
    required: true
  },
  height: {
    type: Number,
    required: true
  }
});
// 尺寸变动，需要重新设置尺寸
watch([() => props.width, () => props.height], () => {
  if (!isEditor.value) return;
  KonvaService.getIns()?.setSize({ width: props.width, height: props.height });
});

const route = useRoute();
const isEditor = computed(() => route.name == 'editor');

// 监听图层中选择变化
const editorStore = useEditorStore();
watch(
  () => editorStore.selectedIds,
  (ids) => {
    if (!isEditor.value) return;
    KonvaService.getIns()?.selection?.selectNodes(ids, {
      fire: false,
      interactable: false
    });
  }
);

// 监听图层中hover
watch(
  () => editorStore.hovered,
  (cur, pre) => {
    if (!isEditor.value) return;

    const hover = !!cur;
    KonvaService.getIns()?.hover?.toggleHover(hover ? cur : pre, hover);
  }
);

onMounted(() => {
  if (KonvaService.getIns()) return;
  KonvaService.init('_konva', {
    width: props.width,
    height: props.height
  });
});

const watchfaceStore = useWatchfaceStore();

// maml render之后重绘shapes
useEmitterListener(
  EventType['maml:rendered'],
  debounce((options: { reason: string }) => {
    const { reason } = options ?? {};
    if (
      watchfaceOpening.value ||
      watchfaceImageLoading.value ||
      watchfaceExporting.value ||
      ['config-data-changed', 'keep-data'].includes(reason)
    ) {
      return;
    }

    // 配置watchface数据
    const service = KonvaService.getIns();
    if (!service?.enable) return;

    const { width = 0, height = 0 } = watchfaceStore.size;
    if (!width || !height) return;

    const data = editorStore.watchfaceCanvas;
    service.watchfaceCanvas = {
      offsetX: data.offsetX,
      offsetY: data.offsetY,
      scale: data.scale,
      width,
      height
    };

    const attrDisabled = editorStore.attrDisabled;
    const _shapes = Shape.shapeTree(editorStore.themeId, false);
    // 进行排序，文本表盘需要在最底层，当做背景
    const textShapes = _shapes.filter(
      (shape) => shape.type == ShapeType.TextWatchface
    );
    const notTextShapes = _shapes.filter(
      (shape) => shape.type != ShapeType.TextWatchface
    );
    const shapes = [...textShapes, ...notTextShapes];
    service.drawShapes(shapes, {
      selected: editorStore.selectedIds,
      disableDrag: !!attrDisabled,
      disableTransform: !!attrDisabled
    });
  })
);

useEmitterListener(EventType['konva:shapes-select'], (ids: string[]) => {
  const _ids = editorStore.selectedIds;
  const same =
    ids.length == _ids.length && ids.every((item) => _ids.includes(item));
  if (!same) {
    editorStore.select(ids);
  }
});

useEmitterListener(EventType['konva:shape-hover'], (id: string) => {
  editorStore.hovered = id;
});

// 监听删除元素
useEmitterListener(EventType['shape:delete-before'], (shapename: string) => {
  KonvaService.getIns()?.deleteShape(shapename);
});

// 添加快捷键
// 批量删除
const deleteSelectedShapes = () => {
  editorStore.activeShapes
    .filter(
      (shape) =>
        ![
          ShapeType.PhotoAlbumBg,
          ShapeType.TextWatchface,
          ShapeType.VideoWatchface
        ].includes(shape?.type as ShapeType.Image)
    )
    .forEach((shape) => shape?.destroy());
};
// 箭头修改位置
const moveOnKeyboard = (data: { offsetX?: number; offsetY?: number }) => {
  const { offsetX, offsetY } = data;
  editorStore.activeShapes.forEach((shape) => {
    if (
      !shape?.draggable().valid ||
      lockedIdsInCurShapeTree.value.includes(shape.id)
    )
      return;
    shape?.setAttrs({
      x: (shape.attrs.x ?? 0) + (offsetX ?? 0),
      y: (shape.attrs.y ?? 0) + (offsetY ?? 0)
    });
  });
};
useGlobalShortcuts(
  [
    {
      keyboardKeys: ['Delete'],
      handler: () => {
        deleteSelectedShapes();
      }
    },
    {
      keyboardKeys: ['Backspace'],
      handler: () => {
        deleteSelectedShapes();
      }
    },
    {
      keyboardKeys: ['ArrowDown'],
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        moveOnKeyboard({ offsetY: 1 });
      }
    },
    {
      keyboardKeys: ['ArrowUp'],
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        moveOnKeyboard({ offsetY: -1 });
      }
    },
    {
      keyboardKeys: ['ArrowLeft'],
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        moveOnKeyboard({ offsetX: -1 });
      }
    },
    {
      keyboardKeys: ['ArrowRight'],
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        moveOnKeyboard({ offsetX: 1 });
      }
    },
    {
      keyboardKeys: ['ArrowDown', 'Shift'],
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        moveOnKeyboard({ offsetY: 10 });
      }
    },
    {
      keyboardKeys: ['ArrowUp', 'Shift'],
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        moveOnKeyboard({ offsetY: -10 });
      }
    },
    {
      keyboardKeys: ['ArrowLeft', 'Shift'],
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        moveOnKeyboard({ offsetX: -10 });
      }
    },
    {
      keyboardKeys: ['ArrowRight', 'Shift'],
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        moveOnKeyboard({ offsetX: 10 });
      }
    }
  ],
  (e) => {
    const el = e.target as HTMLDivElement;
    if (el.dataset.shapeKeyboard !== 'true') return false;
    return editorStore.selectedIds.length > 0 && !editorStore.attrDisabled;
  }
);

onDeactivated(() => {
  const ins = KonvaService.getIns();
  if (ins) {
    ins.clear();
  }
});
</script>
